import { Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { AuthenticationService } from './shared/services/authentication.service';
import { DeviceService } from './shared/services/device.service';
import { UserService } from './shared/services/user.service';

import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { AppSyncNotification, User, UserStatus } from './shared/models/user';
import { ActivatedRoute, ActivationStart, NavigationEnd, Router } from '@angular/router';
import { LoginResponse } from './shared/models/authentication/login-response';
import { AccountService } from './shared/services/account.service';
import { ModalDataButton, ModalService } from './shared/services/modal.service';
import { ArrangementService } from './shared/services/arrangement.service';

import { environment } from 'src/environments/environment';
import { ServiceProviderService } from './shared/services/service-provider.service';
import { PanelType, SiteService } from './shared/services/site.service';
import { NewAppVersionGQLService } from './shared/services/graphql/new-app-version-gql';
import { filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ArrangementLockedGQLService } from './shared/services/graphql/arrangement-locked-gql';
import { AppVersionService } from './shared/services/app-version.service';
import { NotificationBodyService } from './shared/services/notification-body.service';
import _ from 'lodash';
import moment from 'moment-timezone';
import { FormControl, FormGroup } from '@angular/forms';
import { AsyncHandlerService } from './shared/services/async-handler.service';
import { landingRoute } from './shared/utils/landing-route';

// Add siteService to window for debugging
declare global {
  interface Window {
    siteService: SiteService;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  @HostListener('keypress', ['$event'])
  @HostListener('mousemove', ['$event'])
  onEvent(event: any) {
    if (!this.authenticated) {
      return;
    }

    this.idleTime = 0;

    // Get the authenticated user's do-not-disturb status
    const isDnd: boolean = localStorage.getItem('isDND') === '1';

    // Get the authenticated user's current status
    const currentStatus: UserStatus | null =
      this.userService.userSourceValue && this.userService.userSourceValue.status
        ? this.userService.userSourceValue.status
        : null;

    // Set the authenticated user's status to online and restart away timeout
    if (currentStatus !== UserStatus.Online && !isDnd) {
      this.userService.userSourceValue.status = UserStatus.Online;
      // this.userService.setStatus(UserStatus.Online);
      // this.startInterval();
    }
  }

  idleTime: number = 0;
  idleInterval: any;
  user!: User | null;

  authenticated: boolean;
  deviceIsReady: boolean;
  deviceIsOnline: boolean;
  deviceIsPaused: boolean;
  devicePlatform: string;
  cordovaPlatform: string;

  authenticationChecked$ = this.authenticationService.authenticationChecked;

  subscriptions: Subscription[];

  constructor(
    private authenticationService: AuthenticationService,
    private accountService: AccountService,
    private deviceService: DeviceService,
    private primengConfig: PrimeNGConfig,
    private arrangementService: ArrangementService,
    private serviceProviderService: ServiceProviderService,
    private userService: UserService,
    private modalService: ModalService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private siteService: SiteService,
    private newAppVersionGQLService: NewAppVersionGQLService,
    private arrangementLockedGQLService: ArrangementLockedGQLService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private asyncHandlerService: AsyncHandlerService,
    private appVersionService: AppVersionService,
    private notificationBodyService: NotificationBodyService
  ) {
    this.primengConfig.ripple = true;

    this.authenticated = false;
    this.deviceIsReady = false;
    this.deviceIsOnline = true;
    this.deviceIsPaused = false;
    this.devicePlatform = 'unknown';
    this.cordovaPlatform = 'unknown';

    this.subscriptions = [];

    return;

    const originalConsoleError = console.error;

    console.error = function (...args) {
      originalConsoleError.apply(console, args);

      // Convert error objects to string to ensure they are stored correctly
      const errorStrings = args.map((arg) => {
        if (arg instanceof Error) {
          return `${moment().format('YYYY-MM-DD HH:mm:ss')}\nError: ${arg.message}\nStack: ${arg.stack}`;
        } else {
          if (_.has(arg, 'error.error')) {
            return moment().format('YYYY-MM-DD HH:mm:ss') + '\n' + JSON.stringify(arg.error.error);
          } else if (_.has(arg, 'arg.error')) {
            return moment().format('YYYY-MM-DD HH:mm:ss') + '\n' + JSON.stringify(arg.error);
          } else {
            return moment().format('YYYY-MM-DD HH:mm:ss') + '\n' + JSON.stringify(arg);
          }
        }
      });

      // Get existing errors from local storage
      const existingErrors = JSON.parse(localStorage.getItem('loggedErrors') || '[]');

      // Add the new error to the start of the array
      existingErrors.unshift(errorStrings.join('\n\n'));

      if (navigator && navigator.userAgent) {
        existingErrors.unshift('User Agent: ' + navigator.userAgent + '\n\n');
      }

      // Platform is deprecated, I know. But it's still useful for debugging
      if (navigator && navigator.platform) {
        existingErrors.unshift('Platform: ' + navigator.platform + '\n\n');
      }

      // Keep only the last 100 errors
      if (existingErrors.length > 100) {
        existingErrors.length = 100;
      }

      // Update local storage
      localStorage.setItem('loggedErrors', JSON.stringify(existingErrors));
    };
  }

  ngOnInit() {
    // Maintenance mode
    // this.modalService.generic({
    //   title: 'Scheduled Maintenance in Progress',
    //   copy: [
    //     'Conductor is currently offline for scheduled maintenance.',
    //     '<strong>Expected completion:</strong> 2am Monday (AEDT)',
    //     '<strong>Updates and progress:</strong> Check #conductor-support in Slack',
    //     '<strong>Urgent support:</strong> Contact Ivan directly',
    //   ],
    //   buttons: [],
    //   preventClose: true,
    // });

    this.siteService.addSubscriptionLog(this, 'app.component.ts->ngOnInit->this.router.events');

    this.router.events
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'app.component.ts->ngOnInit->this.router.events'
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (event.id === 1) {
            if (!event.url.includes('dashboard')) {
              this.siteService.unBlockUi();
            }
          }
        }

        if (event instanceof ActivationStart) {
          if (event.snapshot.data.hideMainNavigation) {
            this.siteService.closePanel(PanelType.MainNavigationMenu);
          } else {
            this.siteService.openPanel(PanelType.MainNavigationMenu);
          }
        }
      });

    this.authenticationService.isAuthenticated.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (isAuthenticated) => {
        if (isAuthenticated === null) {
          return;
        }

        if (isAuthenticated === true) {
          const user = (this.user = this.authenticationService.currentUserValue);

          if (user) {
            const status = isAuthenticated ? UserStatus.Online : UserStatus.Offline;

            this.userService.set(user);

            if (!user.isActivated) {
              const modal = this.modalService.activateAccount();
            }

            this.userService.setNotifications(user);
          }

          if (!this.authenticated) {
            forkJoin([
              this.userService.getRegions(),
              this.userService.getRoles(),
              this.userService.retrieveAll(),
              this.accountService.get(),
              // Service providers (supplier contacts, pricing, document links) are arranger
              // data. A content-only account never uses them and the server refuses it them
              // (conductor-server #267), so it must not ask: one refusal here fails the whole
              // start-up and leaves the "Loading Conductor" screen up.
              this.userService.userSourceValue?.isContentManagerOnly()
                ? of([])
                : this.serviceProviderService.getAll(),
            ]).subscribe({
              next: (res) => {
                if (this.router.url === '/') {
                  // A content-only account goes where login sends it (Website). Without the
                  // provider fetch above, this can now finish before login's own redirect
                  // commits, and the arrangements list would be refused to it (#267).
                  const user = this.userService.userSourceValue;
                  if (user?.isContentManagerOnly()) {
                    this.router.navigateByUrl(landingRoute(user));
                  } else {
                    this.router.navigate(['dashboard', 'arrangements', 'list']);
                  }
                }

                this.siteService.criticalDataReady();
                this.siteService.unBlockUi();
              },
            });
          }

          this.authenticated = true;
        } else if (isAuthenticated === false) {
          this.siteService.unBlockUi();

          const token = localStorage.getItem('token');

          if (token) {
            this.modalService.error({
              title: 'Authentication Error',
              message: [
                'It looks like your session has expired.',
                'Please login again to continue.',
              ],
            });
          }
        }
      },
      error: (err) => {
        this.siteService.unBlockUi();

        this.modalService.error(err.error.error);
      },
    });

    this.deviceService.deviceIsReady.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (device) => {
        this.deviceIsReady = true;
        this.devicePlatform = device.platform;
      },
    });

    this.deviceService.devicePlatform.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (platform) => {
        this.cordovaPlatform = platform;
      },
    });

    this.deviceService.deviceOnOffline.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.deviceIsOnline = false;
      },
    });

    this.deviceService.deviceOnOnline.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.deviceIsOnline = true;
      },
    });

    this.deviceService.deviceOnPause.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        // if(this.idleInterval){
        //   clearInterval(this.idleInterval);
        // }
        // this.deviceIsPaused = true;
        // this.changeStatus(UserStatus.Away);
      },
    });

    this.deviceService.deviceOnResume.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        // this.deviceIsPaused = false;
        // if (this.user) {
        //   this.startInterval();
        //   this.changeStatus(UserStatus.Online);
        // }
      },
    });

    this.newAppVersionGQLService
      .subscribe()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data: any) => {
          this.onNewVersion(data.data.onNotifyNewAppVersion);
        },
      });

    this.siteService.addSubscriptionLog(
      this,
      'app.component.ts->ngOnInit->this.authenticationService.isAuthenticated'
    );

    this.authenticationService.isAuthenticated
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'app.component.ts->ngOnInit->this.authenticationService.isAuthenticated'
          )
        ),
        takeUntil(this.unsubscribe$),
        filter((isAuthenticated) => !!isAuthenticated),
        switchMap(() => this.userService.user$),
        switchMap((user) => user.notification$),
        filter((notification) => !!notification)
      )
      .subscribe({
        next: (data: AppSyncNotification) => {
          this.onNewNotification(data);
        },
      });

    this.authenticationService.verifyAuthenticationToken().subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  startInterval() {
    // Commented out by Ivan because we don't use this any more
    // if (this.idleInterval) {
    //   clearInterval(this.idleInterval);
    // }
    // const interval = environment.status.interval || 1000;
    // const awayStatusTimeout = environment.status.awayTimeout;
    // this.idleTime = 0;
    // this.idleInterval = setInterval(() => {
    //   this.idleTime += interval;
    //   if (this.idleTime >= awayStatusTimeout) {
    //     this.changeStatus(UserStatus.Away);
    //     clearInterval(this.idleInterval);
    //   } else {
    //     // Get the authenticated user's do-not-disturb status
    //     const isDnd: boolean = localStorage.getItem('isDND') === '1';
    //     if (!isDnd) {
    //       this.changeStatus(UserStatus.Online);
    //     }
    //   }
    // }, interval);
  }

  changeStatus(status: UserStatus) {
    // Commented out by Ivan because we don't use this any more
    // const user = this.authenticationService.currentUserValue;
    // if (user && localStorage.getItem('isDND') !== '1') {
    //   this.userService.setStatus(status);
    // }
  }

  /**
   * AppSync `onNotifyNewAppVersion`. The service fetches the release record,
   * shows the update banner and — if the release was sent as an alert — opens
   * the release-notes modal once. The banner replaces the old 15-minute
   * "remind me" timer.
   */
  private onNewVersion(versionInfo: { version?: string; changelog?: string }) {
    this.appVersionService.onPush(versionInfo);
  }

  private async onNewNotification(notification: AppSyncNotification) {
    console.log('onNewNotification', notification);

    // Execute async handler if any
    if (notification.asyncHandlerId) {
      await this.handleAsyncHandler(notification);
      return;
    }

    // Otherwise, go with default behaviour
    if (notification.popup) {
      await this.handlePopupNotification(notification);
    }

    if (notification.eventDetails) {
      await this.handleEvent(notification);
    }
  }

  private async handleAsyncHandler(notification: AppSyncNotification) {
    if (!this.asyncHandlerService.hasHandler(notification.asyncHandlerId!)) {
      console.error(`Async handler ${notification.asyncHandlerId} not found`);
      return;
    }

    await this.asyncHandlerService.executeHandler(notification.asyncHandlerId!);
  }

  private async handlePopupNotification(notification: AppSyncNotification) {
    // The Notification page sends Markdown (slides → carousel, else a plain
    // dialog); the AppSync schema has no format field, so the hint rides in
    // eventDetails. Server-generated popups (Xero, tasks) are still HTML.
    const hints = this.pushHints(notification);

    this.notificationBodyService.showPopup(notification.notification || '', {
      format: hints.format === 'md' ? 'md' : null,
      display: hints.display === 'fullscreen' ? 'fullscreen' : null,
    });
  }

  /** format / display hints the server tucks into eventDetails (the AppSync schema has no such fields). */
  private pushHints(notification: AppSyncNotification): { format?: string; display?: string } {
    if (!notification.eventDetails) return {};
    try {
      const parsed = JSON.parse(notification.eventDetails);
      return { format: parsed.format, display: parsed.display };
    } catch {
      return {};
    }
  }

  private async handleEvent(notification: AppSyncNotification) {
    const eventDetails = JSON.parse(notification.eventDetails!);

    if (eventDetails.type === 'notification') {
      // Format hint only (see isMarkdownPush); nothing else to do.
    } else if (eventDetails.type === 'tasks_updated') {
      await this.handleTasksUpdatedEvent(eventDetails);
    } else if (eventDetails.type === 'release') {
      // The inbox item for a release. The version broadcast normally lands
      // first; this is the belt to that brace.
      this.appVersionService.check(true);
    }
  }

  private async handleTasksUpdatedEvent(eventDetails: {
    type: 'tasks_updated';
    arrangementId: number;
    arrangementName: string;
    updatedTasks: { taskName: string; taskStatus: string }[];
  }) {
    const currentArrangement = this.arrangementService.currentArrangementSource.value;

    const isCurrentViewingArrangement = currentArrangement?.id === eventDetails.arrangementId;

    const okButton: ModalDataButton = {
      label: 'OK',
      key: 'close',
      class: '',
    };

    const refreshButton: ModalDataButton = {
      label: 'Refresh arrangement',
      key: 'refresh',
      class: '',
      handler: () => {
        return currentArrangement!.retrieve().pipe(
          tap(() => {
            this.modalService.generic({
              title: 'Notification',
              copy: ['Arrangement has been refreshed successfully!'],
              buttons: [okButton],
            });
          })
        );
      },
    };

    const button = isCurrentViewingArrangement ? refreshButton : okButton;

    const updatedTasksList = eventDetails.updatedTasks
      .map(
        (task) =>
          `<li class="mb-1"> Task <b>"${task.taskName}"</b> has been updated to <b>"${task.taskStatus}"</b></li>`
      )
      .join('');

    const message = `
      The following tasks have been updated for the arrangement <b>${eventDetails.arrangementName}</b>: <br />
      <ol>${updatedTasksList}</ol>
      ${isCurrentViewingArrangement ? 'Please refresh the arrangement to get the latest data!' : ''}
    `;

    const modal = this.modalService.generic({
      title: 'Notification',
      copy: [message],
      buttons: [button],
    });

    return modal.onClose.pipe(first()).toPromise();
  }
}
